import { z } from 'zod';

export const phoneRegex = /^(\+7|7|8)\d{10}$/;
export const emailRegex = /^[\S@]+@[\S@]+\.[\S@]+$/;
const tin10Regex = /^\d{10}$/;
const tin12Regex = /^\d{12}$/;

// Indexed access into a string and an array gives `string | undefined`
// under noUncheckedIndexedAccess. We've already validated tin's length via
// the regex, but TS can't see that — coefficients.entries() gives a typed
// (number, number) pair, and tin.charAt() returns a guaranteed string.
function validateOrganizationTin(tin: string): boolean {
  if (!tin10Regex.test(tin)) return false;
  const coefficients = [2, 4, 10, 3, 5, 9, 4, 6, 8];
  let sum = 0;
  for (const [i, coef] of coefficients.entries()) {
    sum += parseInt(tin.charAt(i), 10) * coef;
  }
  return (sum % 11) % 10 === parseInt(tin.charAt(9), 10);
}

function validateIndividualTin(tin: string): boolean {
  if (!tin12Regex.test(tin)) return false;
  const c1 = [7, 2, 4, 10, 3, 5, 9, 4, 6, 8];
  let s1 = 0;
  for (const [i, coef] of c1.entries()) {
    s1 += parseInt(tin.charAt(i), 10) * coef;
  }
  if ((s1 % 11) % 10 !== parseInt(tin.charAt(10), 10)) return false;
  const c2 = [3, 7, 2, 4, 10, 3, 5, 9, 4, 6, 8];
  let s2 = 0;
  for (const [i, coef] of c2.entries()) {
    s2 += parseInt(tin.charAt(i), 10) * coef;
  }
  return (s2 % 11) % 10 === parseInt(tin.charAt(11), 10);
}

export const phoneSchema = z
  .string()
  .min(1, 'Введите телефон')
  .transform(v => {
    const digits = v.replace(/\D/g, '');
    if (digits.startsWith('8') && digits.length === 11) return `+7${digits.slice(1)}`;
    if (digits.startsWith('7') && digits.length === 11) return `+${digits}`;
    if (digits.length === 10) return `+7${digits}`;
    return v;
  })
  .pipe(z.string().regex(phoneRegex, 'Формат: +7XXXXXXXXXX'));

export const emailSchema = z
  .string()
  .min(1, 'Введите почту')
  .regex(emailRegex, 'Введите корректную почту');

export const tinSchema = z
  .string()
  .min(1, 'Введите ИНН')
  .refine(v => validateOrganizationTin(v) || validateIndividualTin(v), {
    message: 'ИНН недействителен',
  });

export const personalNameSchema = z.object({
  surname: z.string().min(1, 'Фамилия обязательна'),
  name: z.string().min(1, 'Имя обязательно'),
  middlename: z.string(),
});

export const organizationSchema = z.object({
  organizationName: z.string().min(1, 'Название организации обязательно'),
  inn: tinSchema,
});

export const phoneFieldSchema = z
  .string()
  .min(1, 'Введите телефон')
  .regex(phoneRegex, 'Формат: +7XXXXXXXXXX');

export const consentSchema = z
  .boolean()
  .refine(v => v, 'Необходимо согласие на обработку персональных данных');

export const enquireSchema = z.object({
  name: z.string().min(1, 'Введите имя'),
  phone: z.string().min(1, 'Введите телефон'),
});

export type EnquirePayload = z.infer<typeof enquireSchema>;

export const applyStep1Schema = personalNameSchema.extend(organizationSchema.shape);

export const applyStep2BaseSchema = z.object({
  phone: phoneFieldSchema,
  email: emailSchema,
  consent: consentSchema,
});

export const v2OrderBaseSchema = z.object({
  phone: phoneFieldSchema,
  inn: tinSchema,
});

export const v2CompleteBaseSchema = personalNameSchema.extend({
  email: emailSchema,
  consent: consentSchema,
});
